// PermissionContext.jsx - COMPLETE WITH ALL SIDEBAR MENU/SUBMENU PERMISSIONS
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import Apiurl from "../Environment/Apiurl";

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};

const readCachedAuth = () => {
  try {
    const userStr = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!userStr || !token) {
      return {
        user: null,
        permissions: {},
        role: null,
        branch: null,
        branches: [],
        allBranchAccess: false,
        userId: null,
      };
    }

    const user = JSON.parse(userStr);
    return {
      user,
      permissions:
        user.permissions && Object.keys(user.permissions).length > 0
          ? user.permissions
          : {},
      role: user.role || null,
      branch: user.branchName || user.branchId || null,
      // ✅ Item #18: multi-branch data scoping. Without these, every page
      // that does its own client-side branch filtering only ever knew
      // about a single branch, even for a user configured with multiple
      // branches or "All Branch Access".
      branches: Array.isArray(user.branches) ? user.branches : [],
      allBranchAccess: !!user.allBranchAccess,
      userId: user._id || user.id || localStorage.getItem("userId") || null,
    };
  } catch (e) {
    console.error("Unable to restore cached authentication:", e);
    return {
      user: null,
      permissions: {},
      role: null,
      branch: null,
      branches: [],
      allBranchAccess: false,
      userId: null,
    };
  }
};

export const PermissionProvider = ({ children }) => {
  // Hydrate authentication/permissions synchronously so a browser refresh
  // never renders an already-authorized user as "Access Denied" for one frame.
  const cachedAuth = readCachedAuth();

  const [permissions, setPermissions] = useState(cachedAuth.permissions);
  const [userRole, setUserRole] = useState(cachedAuth.role);
  const [userBranch, setUserBranch] = useState(cachedAuth.branch);
  // ✅ Item #18: multi-branch data scoping
  const [userBranches, setUserBranches] = useState(cachedAuth.branches);
  const [userAllBranchAccess, setUserAllBranchAccess] = useState(
    cachedAuth.allBranchAccess,
  );
  const [userId, setUserId] = useState(cachedAuth.userId);
  const [loading, setLoading] = useState(!cachedAuth.role);
  const [error, setError] = useState(null);
  const cachedPermissionsRef = useRef(cachedAuth.permissions);

  const loadPermissions = useCallback(async () => {
    const hasCachedPermissions =
      Object.keys(cachedPermissionsRef.current || {}).length > 0;

    try {
      setLoading(true);
      setError(null);

      const userStr = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (!userStr || !token) {
        setPermissions({});
        setUserRole(null);
        setUserBranch(null);
        setUserId(null);
        setLoading(false);
        return;
      }

      const user = JSON.parse(userStr);
      setUserRole(user.role);
      setUserBranch(user.branchName || user.branchId || null);
      setUserId(user._id || user.id);

      if (user.role === "Super Admin") {
        setPermissions(getAllPermissions());
        setLoading(false);
        return;
      }

      // IMPORTANT:
      // Do not stop at cached localStorage permissions here.
      // Add/Edit Role changes are stored against the ROLE, while localStorage may
      // still contain the user's old permission snapshot. Always refresh the role
      // permissions first so role changes take effect after page refresh.
      if (user.role) {
        await fetchRolePermissions(user.role, user, {
          preserveExisting: hasCachedPermissions,
        });
        setLoading(false);
        return;
      }

      // Fallback only for users that genuinely have no role but have direct
      // user-level permissions.
      if (user.permissions && Object.keys(user.permissions).length > 0) {
        setPermissions(user.permissions);
        cachedPermissionsRef.current = user.permissions;
        setLoading(false);
        return;
      }

      try {
        const uid = user._id || user.id || localStorage.getItem("userId");
        if (!uid) {
          setError("User ID not found. Please login again.");
          setPermissions({});
          setLoading(false);
          return;
        }

        const userResponse = await axios.get(`${Apiurl}/user/${uid}`);
        if (userResponse.data) {
          // ✅ Item #18: keep branches/allBranchAccess in sync with the
          // server on every permissions refresh, not just at login — so
          // an admin granting/revoking All Branch Access takes effect
          // without the user needing to log out and back in.
          // Old users only have the single `branch` field — fall back to it
          // so the UI sees the same effective branch list as the backend.
          const freshBranches =
            Array.isArray(userResponse.data.branches) &&
            userResponse.data.branches.length
              ? userResponse.data.branches
              : userResponse.data.branch
                ? [userResponse.data.branch]
                : [];
          const freshAllBranchAccess = !!userResponse.data.allBranchAccess;
          setUserBranches(freshBranches);
          setUserAllBranchAccess(freshAllBranchAccess);
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...user,
              branches: freshBranches,
              allBranchAccess: freshAllBranchAccess,
            }),
          );

          if (
            userResponse.data.permissions &&
            Object.keys(userResponse.data.permissions).length > 0
          ) {
            setPermissions(userResponse.data.permissions);
            cachedPermissionsRef.current = userResponse.data.permissions;
            localStorage.setItem(
              "user",
              JSON.stringify({
                ...user,
                permissions: userResponse.data.permissions,
                branches: freshBranches,
                allBranchAccess: freshAllBranchAccess,
              }),
            );
          } else if (userResponse.data.role) {
            await fetchRolePermissions(userResponse.data.role, user, {
              preserveExisting: hasCachedPermissions,
            });
          } else {
            setError("No permissions assigned to your account");
            if (!hasCachedPermissions) setPermissions({});
          }
        } else {
          setError("Failed to load user data");
          if (!hasCachedPermissions) setPermissions({});
        }
      } catch (userError) {
        if (user.role) {
          await fetchRolePermissions(user.role, user, {
            preserveExisting: hasCachedPermissions,
          });
        } else {
          setError("Failed to load permissions");
          if (!hasCachedPermissions) setPermissions({});
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading permissions:", error);
      setError("Failed to load permissions");
      if (!hasCachedPermissions) setPermissions({});
      setLoading(false);
    }
  }, []);

  // Restore/refresh permissions after the provider has mounted. Because the
  // state above is hydrated synchronously, this request never causes a
  // refresh-time "Access Denied" flash for an already logged-in user.
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const fetchRolePermissions = async (roleName, user, options = {}) => {
    try {
      const rolesResponse = await axios.get(`${Apiurl}/roles`);
      const roles = rolesResponse.data;
      const userRoleData = roles.find((r) => r.roleName === roleName);

      if (!userRoleData) {
        setError("Role not found. Please contact administrator.");
        if (!options.preserveExisting) setPermissions({});
        return;
      }

      if (
        userRoleData.permissions &&
        Object.keys(userRoleData.permissions).length > 0
      ) {
        setPermissions(userRoleData.permissions);
        cachedPermissionsRef.current = userRoleData.permissions;
        localStorage.setItem(
          "user",
          JSON.stringify({ ...user, permissions: userRoleData.permissions }),
        );
      } else {
        setError("No permissions assigned to your role");
        if (!options.preserveExisting) setPermissions({});
      }
    } catch (rolesError) {
      if (rolesError.response?.status === 403) {
        setError("Cannot load permissions. Please contact administrator.");
      } else {
        setError("Failed to load role permissions");
      }
      // Keep the last known-good permissions during a transient refresh
      // failure. Clearing them here is what caused "Access Denied" after
      // browser refreshes for otherwise-authorized users.
      if (!options.preserveExisting) setPermissions({});
    }
  };

  // ── Permission checkers ───────────────────────────────────────────────────
  const canView = (module) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    if (module === "branchMaster") return true;
    const hasAccess = permissions?.[module]?.view === true;
    if (!hasAccess)
      console.log(`Permission denied: ${userRole} cannot view ${module}`);
    return hasAccess;
  };

  const canAdd = (module) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    const hasAccess = permissions?.[module]?.create === true;
    if (!hasAccess)
      console.log(`Permission denied: ${userRole} cannot create ${module}`);
    return hasAccess;
  };

  const canEdit = (module) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    const hasAccess = permissions?.[module]?.edit === true;
    if (!hasAccess)
      console.log(`Permission denied: ${userRole} cannot edit ${module}`);
    return hasAccess;
  };

  const canDelete = (module) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    const hasAccess = permissions?.[module]?.delete === true;
    if (!hasAccess)
      console.log(`Permission denied: ${userRole} cannot delete ${module}`);
    return hasAccess;
  };

  // Distinct from canDelete — controls the multi-select "delete many at once"
  // UI (BulkDeleteBar / row checkboxes), independent of single-record delete.
  // A role can allow deleting one record at a time without allowing bulk
  // selection, or vice versa.
  const canBulkDelete = (module) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    const hasAccess = permissions?.[module]?.bulkDelete === true;
    if (!hasAccess)
      console.log(
        `Permission denied: ${userRole} cannot bulk-delete ${module}`,
      );
    return hasAccess;
  };

  const hasPermission = (module, action) => {
    if (!userRole) return false;
    if (userRole === "Super Admin") return true;
    return permissions?.[module]?.[action] === true;
  };

  // ── Action-specific checkers (import / export / download / print / ────────
  //    screenshot) — thin wrappers around hasPermission for readability at
  //    call sites, mirroring canView/canAdd/canEdit/canDelete above. Note
  //    "screenshot" here means the in-app "capture as image" (html2canvas)
  //    feature, not an OS-level screenshot — that can never be blocked by
  //    a web app.
  const canImport = (module) => hasPermission(module, "import");
  const canExport = (module) => hasPermission(module, "export");
  const canDownload = (module) => hasPermission(module, "download");
  const canPrint = (module) => hasPermission(module, "print");
  const canScreenshot = (module) => hasPermission(module, "screenshot");

  const hasAnyPermission = (module) => {
    if (userRole === "Super Admin") return true;
    const modulePerms = permissions?.[module];
    if (!modulePerms) return false;
    return Object.values(modulePerms).some((val) => val === true);
  };

  const canAccessBranch = (branchId) => {
    if (userRole === "Super Admin" || userAllBranchAccess) return true;
    if (!branchId) return true;
    // ✅ Item #18: check membership across ALL assigned branches, not
    // just the single primary one.
    const allBranchIds = [
      userBranch,
      ...(userBranches || []).map((b) => (typeof b === "object" ? b?._id : b)),
    ]
      .filter(Boolean)
      .map((b) => b.toString());
    return allBranchIds.includes(branchId.toString());
  };

  const getBranchFilter = () => {
    if (userRole === "Super Admin" || userAllBranchAccess) return null;
    // ✅ Item #18: return every assigned branch, not just the primary one,
    // so pages that call this to build a query/filter correctly include
    // all of a multi-branch user's branches.
    const allBranchIds = [
      userBranch,
      ...(userBranches || []).map((b) => (typeof b === "object" ? b?._id : b)),
    ].filter(Boolean);
    return [...new Set(allBranchIds.map((b) => b.toString()))];
  };

  const reloadPermissions = () => loadPermissions();

  const clearPermissions = () => {
    setPermissions({});
    setUserRole(null);
    setUserBranch(null);
    setUserBranches([]);
    setUserAllBranchAccess(false);
    setUserId(null);
    setLoading(false);
    setError(null);
  };

  const value = {
    permissions,
    userRole,
    userBranch,
    userBranches,
    userAllBranchAccess,
    userId,
    loading,
    error,
    canView,
    canAdd,
    canEdit,
    canDelete,
    canBulkDelete,
    canImport,
    canExport,
    canDownload,
    canPrint,
    canScreenshot,
    canAccessBranch,
    getBranchFilter,
    hasPermission,
    hasAnyPermission,
    reloadPermissions,
    clearPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ALL PERMISSIONS — matches every ACTIVE sidebar menu/submenu key exactly
// ─────────────────────────────────────────────────────────────────────────────
const getAllPermissions = () => ({
  // ── Dashboard ─────────────────────────────────────────────────────────────
  dashboard: { view: true, create: true, edit: true, delete: true },

  // ── Master ────────────────────────────────────────────────────────────────
  roles: { view: true, create: true, edit: true, delete: true },
  users: { view: true, create: true, edit: true, delete: true },
  branchMaster: { view: true, create: true, edit: true, delete: true },
  brand: { view: true, create: true, edit: true, delete: true },
  department: { view: true, create: true, edit: true, delete: true },
  colors: { view: true, create: true, edit: true, delete: true },
  documents: { view: true, create: true, edit: true, delete: true },
  catalog: { view: true, create: true, edit: true, delete: true },

  // ── Leads Management ──────────────────────────────────────────────────────
  enquiryList: { view: true, create: true, edit: true, delete: true },
  newEnquiry: { view: true, create: true, edit: true, delete: true },
  followUps: { view: true, create: true, edit: true, delete: true },
  siteVisit: { view: true, create: true, edit: true, delete: true },
  quotation: { view: true, create: true, edit: true, delete: true },
  future: { view: true, create: true, edit: true, delete: true },
  estimate: { view: true, create: true, edit: true, delete: true },
  siteDesigner: { view: true, create: true, edit: true, delete: true },

  // ── HR Management ─────────────────────────────────────────────────────────
  employeeMaster: { view: true, create: true, edit: true, delete: true },
  idCard: { view: true, create: true, edit: true, delete: true }, // ← ADDED: standalone ID Card module
  attendances: { view: true, create: true, edit: true, delete: true },
  holidays: { view: true, create: true, edit: true, delete: true },
  travelAllowance: { view: true, create: true, edit: true, delete: true },
  leaveManagement: { view: true, create: true, edit: true, delete: true },
  taskManagement: { view: true, create: true, edit: true, delete: true },
  salaryManagement: { view: true, create: true, edit: true, delete: true },
  employeeAdvance: { view: true, create: true, edit: true, delete: true },
  payslip: { view: true, create: true, edit: true, delete: true },

  // ── Clients & Projects ────────────────────────────────────────────────────
  clients: { view: true, create: true, edit: true, delete: true },
  projects: { view: true, create: true, edit: true, delete: true },

  // ── Crafts Men ────────────────────────────────────────────────────────────
  carpenterMaster: { view: true, create: true, edit: true, delete: true },
  agents: { view: true, create: true, edit: true, delete: true },
  jobAssigned: { view: true, create: true, edit: true, delete: true },
  workCompletion: { view: true, create: true, edit: true, delete: true },
  carpenteradvance: { view: true, create: true, edit: true, delete: true },

  // ── Inventory ─────────────────────────────────────────────────────────────
  product: { view: true, create: true, edit: true, delete: true },
  purchase: { view: true, create: true, edit: true, delete: true },
  stock: { view: true, create: true, edit: true, delete: true },
  damage: { view: true, create: true, edit: true, delete: true },
  OrderDetail: { view: true, create: true, edit: true, delete: true },

  // ── Warehouse ─────────────────────────────────────────────────────────────
  warehouse: { view: true, create: true, edit: true, delete: true },

  // ── Transport ─────────────────────────────────────────────────────────────
  vehicles: { view: true, create: true, edit: true, delete: true },
  drivers: { view: true, create: true, edit: true, delete: true },
  locations: { view: true, create: true, edit: true, delete: true },
  shipments: { view: true, create: true, edit: true, delete: true },
  shipmentHistory: { view: true, create: true, edit: true, delete: true },
  liveTracking: { view: true, create: true, edit: true, delete: true },
  transportSalary: { view: true, create: true, edit: true, delete: true },

  // ── Distributor (Dealer) ──────────────────────────────────────────────────
  dealer: { view: true, create: true, edit: true, delete: true },
  trader: { view: true, create: true, edit: true, delete: true },
  dealerOrders: { view: true, create: true, edit: true, delete: true },

  // ── Billing ───────────────────────────────────────────────────────────────
  Billing: { view: true, create: true, edit: true, delete: true },

  // ── Digital Marketing ─────────────────────────────────────────────────────
  digitalmarketing: { view: true, create: true, edit: true, delete: true },

  // ── Assets & Expenses ─────────────────────────────────────────────────────
  assets: { view: true, create: true, edit: true, delete: true },
  expenses: { view: true, create: true, edit: true, delete: true },

  // ── CCTV ──────────────────────────────────────────────────────────────────
  cctv: { view: true, create: true, edit: true, delete: true },

  // ── Greetings (payment key) ───────────────────────────────────────────────
  payment: { view: true, create: true, edit: true, delete: true },

  // ── Reports (per-tab) ─────────────────────────────────────────────────────
  reportsSales: { view: true, create: true, edit: true, delete: true },
  reportsPurchase: { view: true, create: true, edit: true, delete: true },
  reportsStock: { view: true, create: true, edit: true, delete: true },
  reportsProducts: { view: true, create: true, edit: true, delete: true },
  reportsProject: { view: true, create: true, edit: true, delete: true },
  reportsLead: { view: true, create: true, edit: true, delete: true },
  reportsDealer: { view: true, create: true, edit: true, delete: true },
  reportsEmployee: { view: true, create: true, edit: true, delete: true },
  reportsAttendance: { view: true, create: true, edit: true, delete: true },
  reportsCarpenter: { view: true, create: true, edit: true, delete: true },
  reportsExpense: { view: true, create: true, edit: true, delete: true },
  reportsDamage: { view: true, create: true, edit: true, delete: true },
  reportsShipment: { view: true, create: true, edit: true, delete: true },
  reportsSalary: { view: true, create: true, edit: true, delete: true },
  reportsClients: { view: true, create: true, edit: true, delete: true },
  reportsTasks: { view: true, create: true, edit: true, delete: true },
  reportsPurchaseReturn: { view: true, create: true, edit: true, delete: true },
  reportsSaleReturn: { view: true, create: true, edit: true, delete: true },
  reportsLeave: { view: true, create: true, edit: true, delete: true },
  reportsAllowance: { view: true, create: true, edit: true, delete: true },
  reportsEmployeeAdvance: {
    view: true,
    create: true,
    edit: true,
    delete: true,
  },
  reportsJobAssigned: { view: true, create: true, edit: true, delete: true },
  reportsWorkCompletion: { view: true, create: true, edit: true, delete: true },
  reportsVehicles: { view: true, create: true, edit: true, delete: true },
  reportsDrivers: { view: true, create: true, edit: true, delete: true },
  reportsDriverSalary: { view: true, create: true, edit: true, delete: true },
  reportsAssets: { view: true, create: true, edit: true, delete: true },
  reportsBalanceSheet: { view: true, create: true, edit: true, delete: true },
  reportsSalesLedger: { view: true, create: true, edit: true, delete: true },

  // ── Tally Sync ────────────────────────────────────────────────────────────
  tallySync: { view: true, create: true, edit: true, delete: true },

  // ── Backward-compat keys (old roles may still use these) ─────────────────
  leads: { view: true, create: true, edit: true, delete: true },
  reports: { view: true, create: true, edit: true, delete: true },
  transport: { view: true, create: true, edit: true, delete: true },
  transportAdvance: { view: true, create: true, edit: true, delete: true },
  employeeRole: { view: true, create: true, edit: true, delete: true },
  shift: { view: true, create: true, edit: true, delete: true },
  orderTracking: { view: true, create: true, edit: true, delete: true },
  tally: { view: true, create: true, edit: true, delete: true },
  biometric: { view: true, create: true, edit: true, delete: true },
  dealerTarget: { view: true, create: true, edit: true, delete: true },
  carpenterAttendances: { view: true, create: true, edit: true, delete: true },
  carpenterSalary: { view: true, create: true, edit: true, delete: true },
});
